import React, { useState, useEffect, useMemo } from 'react';
import { FaChartPie, FaCheckCircle, FaChevronUp, FaChevronDown, FaFilePdf } from 'react-icons/fa';
import { useFinance } from '../context/FinanceContext';
import { generateMonthlyReport } from '../utils/reportGenerator';

const MonthlyGovernance = () => {
    const { targets, updateTargets, getStats, settings, updateSettings, transactions, goals } = useFinance();
    const [view, setView] = useState(null);
    const [form, setForm] = useState({});
    const [generating, setGenerating] = useState(false);

    const stats = getStats();

    useEffect(() => {
        const today = new Date();
        const date = today.getDate();
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

        if (date === 1) setView('setup');
        else if (date >= 28 || date === lastDay) setView('report');
    }, []);

    useEffect(() => {
        if (targets) {
            setForm({
                monthlyIncome: targets.monthlyIncome || 0,
                monthlySpendCap: targets.monthlySpendCap || 0,
                monthlySavingsGoal: targets.monthlySavingsGoal || 0,
            });
        }
    }, [targets]);

    // AI suggested targets: last month + 5% growth
    const suggestions = useMemo(() => {
        const perf = targets?.lastMonthPerformance || {};
        return {
            income: perf.income ? Math.round(perf.income * 1.05) : targets?.monthlyIncome || 0,
            spendCap: perf.spend ? Math.round(perf.spend * 0.95) : targets?.monthlySpendCap || 0,
            savingsGoal: perf.savings ? Math.round(perf.savings * 1.05) : targets?.monthlySavingsGoal || 0,
            incomeLabel: stats.income < (targets?.monthlyIncome || 0) ? 'Increase' : 'Maintain',
            spendLabel: stats.expenses > (targets?.monthlySpendCap || 0) ? 'Reduce' : 'Maintain',
        };
    }, [targets, stats]);

    const handleSealStrategy = async () => {
        await updateTargets(form);
        // Snapshot current month performance for next month comparison
        await updateSettings({
            lastMonthPerformance: {
                income: stats.income,
                spend: stats.expenses,
                savings: stats.profit,
            }
        });
        setView(null);
    };

    const handleGenerateReport = async () => {
        setGenerating(true);
        try {
            generateMonthlyReport({
                stats,
                settings,
                transactions: transactions || [],
                goals: goals || [],
                month: new Date().toISOString().slice(0, 7),
            });
        } catch (e) {
            console.error('Report generation error:', e);
        }
        setGenerating(false);
    };

    // Category analysis for report view
    const categoryAnalysis = useMemo(() => {
        return Object.entries(stats.categoryBreakdown || {})
            .sort((a, b) => b[1] - a[1])
            .map(([cat, amt]) => {
                const budget = settings?.categoryBudgets?.[cat] || 0;
                return { cat, amt, budget, over: budget > 0 && amt > budget };
            });
    }, [stats.categoryBreakdown, settings]);

    if (!view) return null;

    return (
        <div className="fixed inset-0 z-[200] bg-dark-700/80 backdrop-blur-xl flex items-center justify-center p-6">
            <div className="bg-dark-500 w-full max-w-2xl rounded-2xl overflow-hidden shadow-card-hover border border-white/[0.06] relative animate-spring">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/5 blur-[80px] rounded-full"></div>

                {view === 'setup' ? (
                    <div className="p-10 space-y-8">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-glow">
                                <FaCheckCircle />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-frost-900 tracking-tight">Monthly Strategy Setup</h2>
                                <p className="text-xs text-frost-400 font-medium mt-1">Configure targets for this session</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 bg-white/[0.03] p-8 rounded-xl border border-white/[0.06]">
                            <div className="space-y-2">
                                <p className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Previous Inflow</p>
                                <p className="text-2xl font-bold text-frost-900 tracking-tight">₹{(targets.lastMonthPerformance?.income || 0).toLocaleString()}</p>
                                <span className="text-[10px] font-bold text-accent-400 bg-accent-500/10 px-3 py-1 rounded-full border border-accent-500/20">
                                    AI: {suggestions.incomeLabel} → ₹{suggestions.income.toLocaleString()}
                                </span>
                            </div>
                            <div className="space-y-2 text-right">
                                <p className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Previous Outflow</p>
                                <p className="text-2xl font-bold text-frost-900 tracking-tight">₹{(targets.lastMonthPerformance?.spend || 0).toLocaleString()}</p>
                                <span className="text-[10px] font-bold text-gold-400 bg-gold-500/10 px-3 py-1 rounded-full border border-gold-500/20">
                                    AI: {suggestions.spendLabel} → ₹{suggestions.spendCap.toLocaleString()}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-3 gap-4">
                                <InputField label="Target Inflow" value={form.monthlyIncome} onChange={v => setForm({ ...form, monthlyIncome: v })} />
                                <InputField label="Spend Cap" value={form.monthlySpendCap} onChange={v => setForm({ ...form, monthlySpendCap: v })} />
                                <InputField label="Savings Goal" value={form.monthlySavingsGoal} onChange={v => setForm({ ...form, monthlySavingsGoal: v })} />
                            </div>
                            <button onClick={() => {
                                setForm({ monthlyIncome: suggestions.income, monthlySpendCap: suggestions.spendCap, monthlySavingsGoal: suggestions.savingsGoal });
                            }} className="w-full py-2.5 border border-accent-500/20 text-accent-400 font-semibold rounded-xl hover:bg-accent-500/10 transition-all text-xs">
                                Apply AI Suggestions
                            </button>
                            <button onClick={handleSealStrategy}
                                className="w-full py-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow hover:shadow-glow-lg transition-all active:scale-[0.98] text-sm">
                                Seal Monthly Strategy
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-10 space-y-8 max-h-[90vh] overflow-y-auto no-scrollbar relative z-10">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-glow">
                                <FaChartPie />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-frost-900 tracking-tight">End of Session Analysis</h2>
                                <p className="text-xs text-accent-400 font-medium mt-1">Strategic Audit Active</p>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <AnalysisMetric label="Inflow Utilization" current={stats.income} target={targets.monthlyIncome || 1}
                                icon={<FaChevronUp />} color="text-accent-400" />
                            <AnalysisMetric label="Expense Discipline" current={stats.expenses} target={targets.monthlySpendCap || 1}
                                icon={<FaChevronDown />} color="text-gold-400" reverse />

                            {/* Category Level Analysis */}
                            {categoryAnalysis.length > 0 && (
                                <div className="bg-white/[0.03] p-6 rounded-xl border border-white/[0.06] space-y-3">
                                    <h4 className="font-bold text-accent-400 text-xs uppercase tracking-widest border-b border-white/[0.06] pb-2">Category Analysis</h4>
                                    {categoryAnalysis.map(({ cat, amt, budget, over }) => (
                                        <div key={cat} className="flex items-center justify-between text-xs">
                                            <span className="text-frost-500">{cat}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-frost-800 font-semibold">₹{amt.toLocaleString()}</span>
                                                {budget > 0 && (
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${over ? 'text-gold-400 bg-gold-500/10' : 'text-mint-400 bg-mint-500/10'}`}>
                                                        {over ? 'OVER' : 'OK'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="bg-white/[0.03] p-8 rounded-xl border border-white/[0.06]">
                                <h4 className="font-bold text-accent-400 text-xs uppercase tracking-widest mb-3 border-b border-white/[0.06] pb-2">Strategic Synthesis</h4>
                                <p className="text-sm text-frost-800 leading-relaxed font-medium">
                                    Financial discipline was <span className={stats.expenses < (targets.monthlySpendCap || Infinity) ? 'text-mint-400' : 'text-gold-400'}>{stats.expenses < (targets.monthlySpendCap || Infinity) ? 'EXEMPLARY' : 'SUB-OPTIMAL'}</span> this session.
                                    The portfolio achieved <span className="text-accent-400">{((stats.income / Math.max(1, targets.monthlyIncome)) * 100).toFixed(0)}%</span> of its growth target.
                                    {stats.savingsRate > 30 ? ' Outstanding savings discipline!' : stats.savingsRate > 15 ? ' Acceptable savings rate.' : ' Savings rate needs improvement.'}
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button onClick={handleGenerateReport} disabled={generating}
                                    className="flex-1 py-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow hover:shadow-glow-lg transition-all active:scale-[0.98] text-sm flex items-center justify-center gap-2">
                                    <FaFilePdf /> {generating ? 'Generating...' : 'Generate PDF Report'}
                                </button>
                                <button onClick={() => setView(null)}
                                    className="flex-1 py-4 border border-white/[0.06] text-frost-500 font-bold rounded-xl hover:bg-white/[0.03] transition-all active:scale-[0.98] text-sm">
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const InputField = ({ label, value, onChange }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{label}</label>
        <input type="number" value={value || ''}
            onChange={e => onChange(parseInt(e.target.value) || 0)}
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4 font-bold text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 transition-all text-base"
        />
    </div>
);

const AnalysisMetric = ({ label, current, target, icon, color, reverse }) => {
    const progress = target > 0 ? (current / target) * 100 : 0;
    const isHealthy = reverse ? progress < 100 : progress >= 100;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-end">
                <div>
                    <p className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{label}</p>
                    <div className={`flex items-center gap-2 text-2xl font-bold mt-2 tracking-tight ${color}`}>
                        {icon} ₹{current.toLocaleString()}
                    </div>
                </div>
                <div className="text-right">
                    <p className={`text-xs font-bold uppercase tracking-widest border border-current px-3 py-1 rounded-lg ${isHealthy ? 'text-mint-400' : 'text-gold-400'}`}>
                        {isHealthy ? 'OPTIMAL' : 'CRITICAL'}
                    </p>
                </div>
            </div>
            <div className="h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-1000 ${isHealthy ? 'bg-gradient-to-r from-accent-500 to-accent-400 shadow-glow' : 'bg-gold-500'}`}
                    style={{ width: `${Math.min(100, progress)}%` }}></div>
            </div>
        </div>
    );
};

export default MonthlyGovernance;
